import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sendToGHL } from "@/lib/ghl-webhook";
import { Phone, Mail, Clock, MapPin, CheckCircle } from "lucide-react";

const ContactSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    
    // Honeypot protection
    if (formData.get('website')) {
      toast({
        title: "Error",
        description: "Submission blocked - spam detected",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Send to GHL webhook (non-blocking)
      const firstName = formData.get('firstName') as string;
      const lastName = formData.get('lastName') as string;
      
      try {
        await sendToGHL({
          firstName,
          lastName,
          email: formData.get('email') as string,
          phone: formData.get('phone') as string,
          
          // Address fields separated
          addressLine1: formData.get('addressLine1') as string,
          city: formData.get('city') as string,
          state: formData.get('state') as string,
          zipCode: formData.get('zipCode') as string,
          
          // Service details (will be mapped in ghl-webhook.ts)
          serviceType: formData.get('serviceType') as string,
          frequency: formData.get('frequency') as string,
          message: formData.get('message') as string,
          
          // Metadata
          formType: 'quick-estimate',
          pageUrl: window.location.pathname,
          timestamp: new Date().toISOString(),
        });
        console.log('✅ GHL webhook sent successfully');
      } catch (error) {
        console.error('❌ GHL webhook error (non-blocking):', error);
      }

      // Continue with Supabase save (existing functionality)
      const supabaseData = {
        first_name: formData.get('firstName') as string,
        last_name: formData.get('lastName') as string,
        phone: formData.get('phone') as string,
        email: formData.get('email') as string,
        address: `${formData.get('addressLine1')}, ${formData.get('city')}, ${formData.get('state')} ${formData.get('zipCode')}`,
        address_line1: formData.get('addressLine1') as string,
        city: formData.get('city') as string,
        state: formData.get('state') as string,
        zip_code: formData.get('zipCode') as string,
        service_type: formData.get('serviceType') as string,
        frequency: formData.get('frequency') as string,
        message: (formData.get('message') as string) || null
      };

      console.log('📝 Tentando salvar quick estimate no Supabase via Edge Function:', supabaseData);

      const response = await fetch(
        `https://jxmxamlyxcrjbuaojvbw.supabase.co/functions/v1/save-quick-estimate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp4bXhhbWx5eGNyamJ1YW9qdmJ3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTgwNjM5NTgsImV4cCI6MjA3MzYzOTk1OH0.tm6Jh38egTxjRUt6g1hPfm7h2Vy6V5fmUAXy8xKB-GQ`
          },
          body: JSON.stringify(supabaseData)
        }
      );

      if (!response.ok) {
        const responseData = await response.json();
        console.error('❌ Erro Edge Function detalhado:', responseData);
        
        // GHL já recebeu os dados, então apenas alertamos sobre o problema do banco
        toast({
          title: "Partially Submitted",
          description: "Your request was sent but wasn't saved in our database. We'll still process it.",
          variant: "destructive"
        });
        
        // Ainda mostramos o dialog de sucesso pois o GHL recebeu
        setShowSuccessDialog(true);
        (e.target as HTMLFormElement).reset();
        setIsSubmitting(false);
        return;
      }

      const responseData = await response.json();
      console.log('✅ Quick estimate salvo com sucesso no Supabase:', responseData);
      setShowSuccessDialog(true);
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('❌ Erro inesperado ao processar formulário quick estimate:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-muted to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Get Your <span className="bg-gradient-to-r from-brand-gold-dark to-brand-gold-light bg-clip-text text-transparent">Free Estimate</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Ready to experience the Routine Cleaners difference? 
            Contact us today for a personalized quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Request Your Free Estimate</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you within 24 hours.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input id="firstName" name="firstName" placeholder="John" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input id="lastName" name="lastName" placeholder="Doe" required />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="(203) 123-4567" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input id="email" name="email" type="email" placeholder="john@example.com" required />
                  </div>
                </div>

                {/* Honeypot field - hidden from users */}
                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <Label htmlFor="website">Website (leave blank)</Label>
                  <Input id="website" name="website" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addressLine1">Street Address *</Label>
                  <Input id="addressLine1" name="addressLine1" placeholder="123 Main Street" required />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input id="city" name="city" placeholder="Hartford" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input id="state" name="state" placeholder="CT" required maxLength={2} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">ZIP Code *</Label>
                    <Input id="zipCode" name="zipCode" placeholder="06103" required />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="serviceType">Service Type</Label>
                    <select id="serviceType" name="serviceType" required className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">Select service</option>
                      <option value="deep">Deep Cleaning</option>
                      <option value="regular">Regular House Cleaning</option>
                      <option value="one-time">One Time Cleaning</option>
                      <option value="move-in">Move In Cleaning</option>
                      <option value="move-out">Move Out Cleaning</option>
                      <option value="spring">Spring Cleaning</option>
                      <option value="holiday">Holiday Cleaning</option>
                      <option value="post-construction">Post-Construction Cleaning</option>
                      <option value="post-renovation">Post Renovation Cleaning</option>
                      <option value="airbnb">Airbnb Cleaning</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequency</Label>
                    <select id="frequency" name="frequency" required className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">Select frequency</option>
                      <option value="onetime">One-time</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Additional Details</Label>
                  <Textarea 
                    id="message" 
                    name="message"
                    placeholder="Tell us about your specific cleaning needs, property size, any special requirements..."
                    rows={4}
                  />
                </div>

                <Button 
                  type="submit" 
                  variant="hero" 
                  size="xl" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Get My Free Estimate"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-semibold text-foreground">Customer Service</div>
                      <div className="text-muted-foreground">(203) 350-3604</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-semibold text-foreground">Sales</div>
                      <div className="text-muted-foreground">(475) 251-7156</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-semibold text-foreground">Email</div>
                      <div className="text-muted-foreground">contact@routinecleaners.com</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-semibold text-foreground">Office Hours</div>
                      <div className="text-muted-foreground">Monday - Friday: 8:00 AM - 6:00 PM</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-accent" />
                    <div>
                      <div className="font-semibold text-foreground">Service Area</div>
                      <div className="text-muted-foreground">All of Connecticut</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Service */}
            <Card className="border-accent bg-accent/5">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Flexible Scheduling Available</h3>
                <p className="text-muted-foreground mb-4">
                  Need cleaning service this week? We offer flexible scheduling to accommodate your busy lifestyle.
                </p>
                <Button variant="outline-gold" className="w-full" asChild>
                  <a href="tel:+14752517156">Call for Immediate Service</a>
                </Button>
              </CardContent>
            </Card>

            {/* Service Guarantee */}
            <Card className="border-border bg-primary">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-primary-foreground mb-4">Our Promise to You</h3>
                <ul className="space-y-2 text-primary-foreground/90">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    <span>Free, no-obligation estimates</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    <span>100% satisfaction guarantee</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    <span>Licensed and insured</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    <span>Flexible scheduling</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="relative">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <DialogTitle className="text-center text-2xl">Request Submitted Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              Thank you for choosing Routine Cleaners! Here's what happens next.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-center text-muted-foreground">
              Thank you for choosing Routine Cleaners! Here's what happens next:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">1</div>
                <div className="flex-1">
                  <p className="font-medium">We'll review your request</p>
                  <p className="text-sm text-muted-foreground">Our team will carefully analyze your needs</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">2</div>
                <div className="flex-1">
                  <p className="font-medium">We'll contact you within 2-4 hours</p>
                  <p className="text-sm text-muted-foreground">
                    We'll reach out via phone or email with your personalized estimate
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold">3</div>
                <div className="flex-1">
                  <p className="font-medium">Schedule your cleaning</p>
                  <p className="text-sm text-muted-foreground">Choose a convenient time for your service</p>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">Have questions?</p>
              <Button variant="outline" asChild>
                <a href="tel:+14752517156">
                  <Phone className="mr-2 h-4 w-4" />
                  Call us now: (475) 251-7156
                </a>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactSection;
